/* eslint-disable react/prop-types */
import { useAtom } from "jotai";
import omit from "lodash/omit";
import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Layout, Seo, Status } from "~components";
import { Header } from "~components/reservation";
import { getError, getUrl, showToast } from "~lib";

import { AccessModal } from "~components/releases";

import { useFacebookPixelScripts } from "~features/analytics/useThirdpartyScripts";
import {
  asOpenGraphImage,
  competitionSeo,
  CompetitionSeoProps,
} from "~features/competitions/competitionSeo";
import NonSeatedEventReservation from "~features/nonseated-reservation/components/NonSeatedEventReservation";
import { IEventReservationPage } from "~features/reservation/reservation-types";
import { TicketDetailsModal } from "~features/seated-reservation/components/common/modal.TicketDetails";
import { Seated as SeatedReservationPage } from "~features/seated-reservation/reservation.Seated";
import * as actions from "~features/seated-reservation/store/actions/index";
import { QueryParams } from "~features/seated-reservation/types";
import {
  EventAccessStatus,
  ReleaseStatusValue,
  VenueSeating,
} from "~graphql/sdk";
import { useCampaignReportingOpen } from "~hooks/useBroadastCampaignReporting";
import { useEventAccessControl } from "~hooks/useEventAccessControl";
import {
  googleAnalyticsReleaseAtom,
  useGoogleAnalyticsPageView,
} from "~hooks/useGoogleAnalyticsPageView";
import { useReferralCode } from "~hooks/useReferralCode";
import { store } from "~lib/store";
import { PageName } from "~telemetry/enums/AttributeNames";
import usePageLoad from "~telemetry/traces/usePageLoad";
import { useOrganization } from "~hooks";
import { useEventTrackingCodes } from "~hooks/useEventTrackingCodes";

type Query = {
  eventId?: string;
  release?: string;
  slug?: string;
  ownedTickets?: string;
  session?: string;
  posId?: string;
  customer?: string;
  zone?: string;
};

export const getServerSideProps: GetServerSideProps<CompetitionSeoProps> = async (
  context
) => {
  return await competitionSeo(
    store.organization.id,
    context.params?.eventId as string
  );
};

const Reservation = ({ event, release, isAdmin }: IEventReservationPage) => {
  const router = useRouter();
  const query = router.query as Query;
  const { organization, hasFeature } = useOrganization();

  const [hasAccess, setHasAccess] = useState<boolean | undefined>(undefined);

  useEventTrackingCodes();

  // Store referralCode & version in sessionStorage.
  useReferralCode();

  useEffect(() => {
    void router.prefetch("/checkout/[orderId]");
    void router.prefetch("/events/[eventId]");
  }, []);

  useEffect(() => {
    if (router.query?.session !== QueryParams.SessionContinue) {
      actions.resetState();
    }
  }, []);

  useEffect(() => {
    if (release?.requiresAuth !== undefined) {
      setHasAccess(!release.requiresAuth);
    }
  }, [release?.requiresAuth]);

  if (
    hasFeature("showExperienceSales") &&
    event.venue?.seating !== VenueSeating.Seated &&
    (isAdmin || (release?.isActive && hasAccess))
  ) {
    return (
      <NonSeatedEventReservation
        event={event}
        release={release}
        isAdmin={isAdmin}
      />
    );
  }

  return (
    <>
      <Header
        title={event.title}
        startDate={event.startDate as string}
        endDate={event.endDate as string}
        location={
          event.venue?.name &&
          `${event.venue?.name}, ${event.venue?.address?.city}`
        }
        goBack={() => {
          if (query.posId) {
            window.location.href = `${getUrl(organization.slug)}/pos/${
              query.posId
            }`;
          } else {
            void router.push("/");
          }
        }}
        accessibility={event.accessibility}
        supportInfo={organization?.supportInfo}
        image={event.logo}
        description={event.description}
        coordinates={`${event.venue?.location?.lat},${event.venue?.location?.lng}`}
      />
      <Layout layoutType="sub">
        {(isAdmin || (release?.isActive && hasAccess)) && (
          <>
            {event.venue?.seating !== VenueSeating.Seated ? (
              <NonSeatedEventReservation
                event={event}
                release={release}
                isAdmin={isAdmin}
              />
            ) : (
              <SeatedReservationPage
                type="event"
                typeId={query.eventId}
                release={release}
                defaultShowOptions={
                  !query.ownedTickets && !query.session && hasAccess
                }
              />
            )}
            <TicketDetailsModal />
          </>
        )}
        <AccessModal
          hasAccess={hasAccess}
          setHasAccess={setHasAccess}
          release={release}
        />
      </Layout>
    </>
  );
};

const ReservationPage: NextPage<CompetitionSeoProps> = ({
  activeCompetition: { data: activeCompetition },
  eventImageUrls: { data: eventImageUrls },
  eventTitle: { data: eventTitle },
}) => {
  const router = useRouter();
  const { eventId } = router.query as Query;
  useGoogleAnalyticsPageView(
    !!eventId,
    PageName.EVENT_RESERVATION_PAGE,
    eventId
  );
  usePageLoad(
    PageName.EVENT_RESERVATION_PAGE,
    {
      forwardToNest: true,
      eventId: eventId,
    },
    // Only fire the page-load when:
    // - the event is loaded
    !!eventId
  );

  const {
    error,
    event,
    release,
    isAdmin,
    isLoading,
    accessType,
  } = useEventAccessControl();

  useFacebookPixelScripts(event?.pixelId);

  const [, setRelease] = useAtom(googleAnalyticsReleaseAtom);

  useEffect(() => {
    if (release) {
      //  @ts-expect-error atom type issue while using strict:false
      setRelease(release);
    }
  }, [release]);

  const [triggerCampaignReporting, setTriggerCampaignReporting] = useState(
    false
  );

  useCampaignReportingOpen(triggerCampaignReporting);

  /**
   * This effect handles all of the redirects back to the info page, making sure
   * the the query params stay the same.
   */
  useEffect(() => {
    if (isLoading) {
      return;
    }
    let redirect = false;

    // Take all params except the `eventId` because this is part of the url
    let queryParams: Query = omit(router?.query, "eventId");

    if (release?.status.value === ReleaseStatusValue.ReleaseHasEndedDueToDate) {
      queryParams = omit(queryParams, "slug");
    }

    const canViewInactiveRelease: boolean = isAdmin || !!router?.query.posId;

    const isAccessDenied =
      getError(error, "graphQL") === EventAccessStatus.Denied ||
      (accessType !== EventAccessStatus.Allowed &&
        accessType !== EventAccessStatus.AllowedAdminPreview);

    const hasEventEnded =
      getError(error, "graphQL") === EventAccessStatus.EventEnded;

    if (error && !isAccessDenied && !hasEventEnded) {
      if (router?.query.posId) {
        showToast(getError(error, "graphQL"), "error");
        return window.location.reload();
      }
      showToast(getError(error, "graphQL"), "error");
      redirect = true;
    } else if (
      isAccessDenied ||
      !release ||
      (release?.isActive === false && canViewInactiveRelease === false)
    ) {
      redirect = true;
      showToast(
        release?.status.message ?? "This release is not publicly available",
        "error"
      );
    }

    if (redirect) {
      void router.push({
        pathname: `/events/${event?.id}`,
        query: omit(queryParams, "release"),
      });
    } else {
      setTriggerCampaignReporting(true);
    }
  }, [event?.id, release, error, isAdmin, isLoading]);

  const title = activeCompetition
    ? activeCompetition.title
    : "Get your tickets now!";

  return (
    <>
      <Seo
        title="Tickets"
        description="Tickets"
        options={{
          openGraph: {
            title,
            description: eventTitle,
            images: eventImageUrls?.map((url) =>
              asOpenGraphImage(url, eventTitle)
            ),
          },
        }}
      />
      <Status error={error} loading={isLoading || !event?.venue?.seating}>
        <Reservation event={event} release={release} isAdmin={isAdmin} />
      </Status>
    </>
  );
};

export default ReservationPage;
